import React from 'react';
import AccountingAiAssistantPanel from '@/components/accounting/AccountingAiAssistantPanel';

export const AI_ASSISTANT_SHORTCUT = 'Ctrl+Shift+A';

type AccountingShellContextValue = {
  openAiAssistant: () => void;
};

const AccountingShellContext = React.createContext<AccountingShellContextValue | null>(null);

export function useAccountingShell(): AccountingShellContextValue {
  const ctx = React.useContext(AccountingShellContext);
  if (!ctx) {
    throw new Error('useAccountingShell must be used within AccountingShellProvider');
  }
  return ctx;
}

export function useAccountingShellOptional(): AccountingShellContextValue | null {
  return React.useContext(AccountingShellContext);
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return target.isContentEditable;
}

export const AccountingShellProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [aiOpen, setAiOpen] = React.useState(false);

  const openAiAssistant = React.useCallback(() => setAiOpen(true), []);

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!e.ctrlKey || !e.shiftKey) return;
      if (isEditableTarget(e.target)) return;

      const key = e.key.toLowerCase();
      if (key === 'a') {
        e.preventDefault();
        openAiAssistant();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [openAiAssistant]);

  const value = React.useMemo(() => ({ openAiAssistant }), [openAiAssistant]);

  return (
    <AccountingShellContext.Provider value={value}>
      {children}
      <AccountingAiAssistantPanel open={aiOpen} onOpenChange={setAiOpen} />
    </AccountingShellContext.Provider>
  );
};
